import React from 'react';
import { ChevronDown, Trash2, User, Database, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import type { ExpandableLegendSeries } from '../../types';

interface ExpandableLegendSeriesPopoverProps {
  series: ExpandableLegendSeries[];
  focusMode: boolean;
  onToggleSelection: (seriesId: string) => void;
  onToggleFocus: (seriesId: string) => void;
  onToggleTimestamp: (seriesId: string, timestampId: string) => void;
  onDeleteSnapshot?: (snapshotId: string) => void;
  markerLabel?: string; // Dynamic label for markers (e.g., "Timestamps" for sensors, "Markers" for spectrum)
}

/**
 * ExpandableLegendSeriesPopover component
 *
 * Displays a simple popover with a list of timestamp snapshots.
 * - Badge shows selected snapshot label or "Snapshots"
 * - Opens popover with all snapshot items
 * - Each item has a radio button for selection and a delete button
 */
export const ExpandableLegendSeriesPopover: React.FC<ExpandableLegendSeriesPopoverProps> = ({
  series,
  focusMode,
  onToggleSelection,
  onToggleFocus,
  onToggleTimestamp,
  onDeleteSnapshot,
  markerLabel = 'Markers',
}) => {
  // Get selected series
  const selectedSeriesList = series.filter((s) => s.isSelected);
  const selectedSeries = selectedSeriesList[0]; // For single-select mode

  // Badge label - show selected item(s) or "Snapshots"
  let badgeLabel: string;
  if (focusMode && selectedSeriesList.length > 1) {
    // Focus mode with multiple selections: show count with dynamic marker label
    badgeLabel = `${selectedSeriesList.length} ${markerLabel} Selected`;
  } else if (selectedSeriesList.length === 1) {
    // Single selection (or focus mode with one selection): show label
    badgeLabel = selectedSeries.label;
  } else {
    // No selection - use dynamic marker label
    badgeLabel = markerLabel;
  }

  // Get the selected value for RadioGroup (for single-select mode)
  const selectedValue = selectedSeries?.id || '';

  // Wrap onToggleSelection to add logging
  const handleToggleSelection = (seriesId: string) => {
    onToggleSelection(seriesId);
  };

  // Single-select mode: show inline box with checkmark and bold text
  const [isExpanded, setIsExpanded] = React.useState(false);
  const maxVisibleItems = 4;
  const hasMoreItems = series.length > maxVisibleItems;
  const visibleSeries = isExpanded ? series : series.slice(0, maxVisibleItems);
  const hiddenCount = series.length - maxVisibleItems;

  if (!focusMode) {
    return (
      <div className="w-full">
        <div className="py-0">
          {visibleSeries.map((snapshot: any) => {
            const isSelected = snapshot.isSelected;
            return (
              <div
                key={snapshot.id}
                onClick={() => handleToggleSelection(snapshot.id)}
                className={`flex items-center gap-2 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0`}
              >
                {/* Icon - User or Database based on source */}
                <div className="flex-shrink-0">
                  {snapshot.source === 'user' || snapshot.badge === 'user' ? (
                    <User className={`w-4 h-4 ${isSelected ? 'text-gray-700 stroke-[2.5]' : 'text-gray-500'}`} />
                  ) : snapshot.source === 'system' || snapshot.source === 'Datamind' || snapshot.source === 'backend' ? (
                    <Database className={`w-4 h-4 ${isSelected ? 'text-gray-700 stroke-[2.5]' : 'text-gray-500'}`} />
                  ) : null}
                </div>

                {/* Label - bold when selected */}
                <span className={`text-sm flex-1 ${
                  isSelected
                    ? 'font-semibold text-[#01152B] dark:text-gray-100'
                    : 'text-[#01152B] dark:text-gray-200'
                }`}>
                  {snapshot.label}
                </span>

                {/* Checkmark for selected item - on the right */}
                {isSelected && (
                  <Check className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                )}
              </div>
            );
          })}
          {hasMoreItems && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline py-0.5 cursor-pointer"
            >
              {isExpanded ? 'Show less' : `+ ${hiddenCount} more`}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Focus mode: Multi-select with popover
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="w-full inline-flex items-center justify-between gap-1.5 px-3 py-1.5 text-[9px] font-medium rounded-lg border border-[#B1B9C3] bg-white text-[#01152B] hover:bg-gray-50 transition-colors cursor-pointer">
          <span className="flex-1 text-left truncate text-sm">{badgeLabel}</span>
          <ChevronDown size={12} className="text-[#01152B] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[274px] p-0 rounded-lg" side="right" align="start" sideOffset={4}>
        <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
          <div className="py-2 space-y-1">
            {series.map((snapshot: any) => {
              return (
                <div
                  key={snapshot.id}
                  className={`flex items-center gap-3 px-4 py-2 group ${
                    snapshot.isFocused ? 'bg-primary-50 hover:bg-primary-100' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Checkbox for multi-select */}
                  <Checkbox
                    checked={snapshot.isSelected}
                    onCheckedChange={() => handleToggleSelection(snapshot.id)}
                    className="flex-shrink-0"
                  />

                  {/* Icon - User or Database based on source */}
                  <div className="flex-shrink-0">
                    {snapshot.source === 'user' || snapshot.badge === 'user' ? (
                      <User className="w-4 h-4 text-gray-500" />
                    ) : snapshot.source === 'system' || snapshot.source === 'Datamind' || snapshot.source === 'backend' ? (
                      <Database className="w-4 h-4 text-gray-500" />
                    ) : null}
                  </div>

                  {/* Label */}
                  <span
                    className="text-sm text-[#01152B] flex-1 cursor-pointer"
                    onClick={() => handleToggleSelection(snapshot.id)}
                  >
                    {snapshot.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
